(() => {
  // KIK FAQ accordion component
  if (customElements.get('kik-faq-accordion')) return;

  class KikFaqAccordion extends HTMLElement {
    constructor() {
      super();
      this.initialized = false;
      this.resizeObserver = null;
      this.prefersReducedMotion = false;
    }

    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      this.allowMultiple = this.hasAttribute('data-allow-multiple');
      this.openFirst = this.hasAttribute('data-open-first');

      this.prefersReducedMotion =
        window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches || false;

      this.setupResizeObserver();
      this.initAccordionItems();
    }

    setupResizeObserver() {
      if (typeof ResizeObserver === 'undefined') return;

      this.resizeObserver = new ResizeObserver((entries) => {
        entries.forEach((entry) => {
          const panelInner = entry.target;
          const panel = panelInner.closest('.kik-faq-accordion__panel');
          const trigger = panel?.previousElementSibling;

          if (!panel || !trigger || trigger.getAttribute('aria-expanded') !== 'true') {
            return;
          }

          panel.style.maxHeight = this.prefersReducedMotion
            ? 'none'
            : `${panelInner.offsetHeight}px`;
        });
      });
    }

    initAccordionItems() {
      const items = this.querySelectorAll('kik-faq-item');

      items.forEach((item, index) => {
        const trigger = item.querySelector('.kik-faq-accordion__trigger');
        const panel = item.querySelector('.kik-faq-accordion__panel');
        const panelInner = panel?.querySelector('.kik-faq-accordion__panel-inner');

        if (!trigger || !panel || !panelInner) return;

        if (this.resizeObserver) {
          this.resizeObserver.observe(panelInner);
        }

        // Open first item if requested
        if (index === 0 && this.openFirst) {
          requestAnimationFrame(() =>
            this.openAccordionItem(trigger, panel, panelInner, false),
          );
        }

        const handleTriggerActivation = () => {
          const isOpen = trigger.getAttribute('aria-expanded') === 'true';

          if (!this.allowMultiple) {
            this.closeAllExcept(trigger);
          }

          if (isOpen) {
            this.closeAccordionItem(trigger, panel, panelInner);
          } else {
            this.openAccordionItem(trigger, panel, panelInner, true);
          }
        };

        trigger.addEventListener('click', handleTriggerActivation);

        trigger.addEventListener('keydown', (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleTriggerActivation();
          }
        });
      });
    }

    closeAllExcept(activeTrigger) {
      const triggers = this.querySelectorAll('.kik-faq-accordion__trigger');

      triggers.forEach((trigger) => {
        if (trigger === activeTrigger) return;
        if (trigger.getAttribute('aria-expanded') !== 'true') return;

        const panel = trigger.nextElementSibling;
        const panelInner = panel?.querySelector('.kik-faq-accordion__panel-inner');

        if (panel && panelInner) {
          this.closeAccordionItem(trigger, panel, panelInner);
        }
      });
    }

    openAccordionItem(trigger, panel, panelInner, animate) {
      const item = trigger.closest('kik-faq-item');

      trigger.setAttribute('aria-expanded', 'true');
      panel.setAttribute('aria-hidden', 'false');

      if (item) {
        item.setAttribute('data-state', 'open');
      }

      if (this.prefersReducedMotion || !animate) {
        panel.style.maxHeight = 'none';
        panel.setAttribute('data-animate', 'false');
        return;
      }

      const targetHeight = panelInner.offsetHeight;
      panel.setAttribute('data-animate', 'true');
      panel.style.maxHeight = `${targetHeight}px`;
    }

    closeAccordionItem(trigger, panel, panelInner) {
      const item = trigger.closest('kik-faq-item');

      trigger.setAttribute('aria-expanded', 'false');
      panel.setAttribute('aria-hidden', 'true');

      if (item) {
        item.setAttribute('data-state', 'closed');
      }

      if (this.prefersReducedMotion) {
        panel.style.maxHeight = '0';
        panel.setAttribute('data-animate', 'false');
        return;
      }

      const isAnimating = panel.getAttribute('data-animate') === 'true';
      const currentHeight = panelInner.offsetHeight;

      panel.style.maxHeight = `${currentHeight}px`;

      if (!isAnimating) {
        requestAnimationFrame(() => {
          panel.style.maxHeight = '0';
        });
      } else {
        panel.style.maxHeight = '0';
      }

      panel.setAttribute('data-animate', 'true');
    }
  }

  customElements.define('kik-faq-accordion', KikFaqAccordion);

  if (!customElements.get('kik-faq-item')) {
    class KikFaqItem extends HTMLElement {
      constructor() {
        super();
        this.initialized = false;
      }

      connectedCallback() {
        if (this.initialized) return;
        this.initialized = true;
      }
    }

    customElements.define('kik-faq-item', KikFaqItem);
  }


 class KikMarqueeText extends HTMLElement {
    constructor() {
      super();
      if (window.ResizeObserver) {
        new ResizeObserver(this._calculateDuration.bind(this)).observe(this);
      }
    }
    _calculateDuration(entries) {
      const scrollingSpeed = parseInt(this.getAttribute("scrolling-speed") || 5), contentWidth = entries[0].contentRect.width, slowFactor = 1 + (Math.min(1600, contentWidth) - 375) / (1600 - 375);
      this.style.setProperty("--marquee-animation-duration", `${(scrollingSpeed * slowFactor * entries[0].target.querySelector("div").clientWidth / contentWidth).toFixed(3)}s`);
    }
  };
  if (!window.customElements.get("kik-marquee-text")) {
    window.customElements.define("kik-marquee-text", KikMarqueeText);
  }

  class KikContactLink extends HTMLElement {
    constructor() {
      super();
      this.initialized = false;
      this.handleClick = this.handleClick.bind(this);
      this.handleKeydown = this.handleKeydown.bind(this);
    }

    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      if (!this.hasAttribute('role')) {
        this.setAttribute('role', 'link');
      }

      if (!this.hasAttribute('tabindex')) {
        this.tabIndex = 0;
      }

      this.addEventListener('click', this.handleClick);
      this.addEventListener('keydown', this.handleKeydown);
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.handleClick);
      this.removeEventListener('keydown', this.handleKeydown);
    }

    get buttonUrl() {
      return (this.dataset.buttonUrl || '').trim();
    }

    handleKeydown(event) {
      if (event.key !== 'Enter' && event.key !== ' ') return;
      event.preventDefault();
      this.handleClick(event);
    }

    handleClick(event) {
      const url = this.buttonUrl;

      if (!url) return;

      if (url.startsWith('#') || url.startsWith('.')) {
        event.preventDefault();

        const clicked = this.triggerSelectorTarget(url);

        if (!clicked) {
          console.warn(`kik-contact-link: target not found for selector "${url}"`);
        }

        return;
      }

      if (url.startsWith('tel:') || url.startsWith('mailto:')) {
        window.location.href = url;
        return;
      }

      window.location.href = url;
    }

    triggerSelectorTarget(selector) {
      const clickWithin = (root) => {
        const target = root?.querySelector(selector);
        if (!target) return false;
        target.click();
        return true;
      };

      if (clickWithin(document)) return true;

      const iframes = document.querySelectorAll('iframe');

      for (const frame of iframes) {
        try {
          const frameDoc = frame.contentDocument || frame.contentWindow?.document;
          if (!frameDoc) continue;
          if (clickWithin(frameDoc)) return true;
        } catch (error) {
          // Ignore cross-origin frames we cannot access
          continue;
        }
      }

      return false;
    }
  }

  if (!customElements.get('kik-contact-link')) {
    customElements.define('kik-contact-link', KikContactLink);
  }

  // Kik countdown timer -----------------------------------

  function kikCountdownPad(value) {
    value = Math.floor(value);
    if (value < 0) value = 0;
    return value < 10 ? '0' + value : String(value);
  }

  function kikCountdownParseDateTime(value) {
    if (!value) return null;

    let text = String(value).trim();
    if (!text) return null;

    // Expected format: YYYY-MM-DDTHH:MM:SS+HH:MM
    // Example: 2025-12-01T23:59:00+08:00 or 2025-12-01T15:59:00+00:00
    const match = text.match(
      /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})([+\-]\d{2}:\d{2})$/
    );

    if (!match) return null;

    const [, y, m, d, hh, mm, ss, offset] = match;

    const year = parseInt(y, 10);
    const month = parseInt(m, 10) - 1;
    const day = parseInt(d, 10);
    const hour = parseInt(hh, 10);
    const minute = parseInt(mm, 10);
    const second = parseInt(ss, 10);

    if (
      Number.isNaN(year) ||
      Number.isNaN(month) ||
      Number.isNaN(day) ||
      Number.isNaN(hour) ||
      Number.isNaN(minute) ||
      Number.isNaN(second)
    ) {
      return null;
    }

    const sign = offset[0] === '-' ? -1 : 1;
    const tzHours = parseInt(offset.slice(1, 3), 10);
    const tzMinutes = parseInt(offset.slice(4, 6), 10);

    if (Number.isNaN(tzHours) || Number.isNaN(tzMinutes)) {
      return null;
    }

    const offsetMinutes = sign * (tzHours * 60 + tzMinutes);
    const utcMs =
      Date.UTC(year, month, day, hour, minute, second) -
      offsetMinutes * 60 * 1000;

    return new Date(utcMs);
  }

  class KikCountdown extends HTMLElement {
    constructor() {
      super();
      this._initialized = false;
      this._interval = null;
    }

    connectedCallback() {
      if (this._initialized) return;
      this._initialized = true;

      this._daysEl = this.querySelector('[data-kik-countdown-days]');
      this._hoursEl = this.querySelector('[data-kik-countdown-hours]');
      this._minutesEl = this.querySelector('[data-kik-countdown-minutes]');
      this._secondsEl = this.querySelector('[data-kik-countdown-seconds]');

      if (!this._hoursEl || !this._minutesEl || !this._secondsEl) return;

      this._startCountdown();
    }

    disconnectedCallback() {
      this._clearInterval();
    }

    static get observedAttributes() {
      return [
        'data-kik-countdown-duration-days',
        'data-kik-countdown-duration-hours',
        'data-kik-countdown-duration-minutes',
        'data-kik-countdown-duration-seconds',
        'data-kik-countdown-start',
        'data-kik-countdown-cycle-days',
        'data-kik-countdown-target-id',
        'data-kik-countdown-hard-end',
        'data-kik-countdown-manual-end',
      ];
    }

    attributeChangedCallback(name, oldValue, newValue) {
      if (!this._initialized) return;
      if (oldValue === newValue) return;
      this._startCountdown();
    }

    _clearInterval() {
      if (this._interval) {
        clearInterval(this._interval);
        this._interval = null;
      }
    }

    _getTargetElement() {
      const targetId = this.getAttribute('data-kik-countdown-target-id');
      if (!targetId) return null;
      return document.getElementById(targetId);
    }

    _hideTarget() {
      const target = this._getTargetElement();
      if (!target) return;
      target.setAttribute('hidden', 'hidden');
    }

    _showTarget() {
      const target = this._getTargetElement();
      if (!target) return;
      target.removeAttribute('hidden');
    }

    _getCycleConfig() {
      const startAttr = this.getAttribute('data-kik-countdown-start');
      const cycleDaysAttr = this.getAttribute('data-kik-countdown-cycle-days') || '0';
      const timezoneOffsetAttr =
        this.getAttribute('data-kik-countdown-timezone-offset') || '+00:00';

      const startDate = kikCountdownParseDateTime(
        startAttr ? startAttr + timezoneOffsetAttr : ''
      );

      let cycleDays = parseInt(cycleDaysAttr, 10);
      if (Number.isNaN(cycleDays) || cycleDays < 0) {
        cycleDays = 0;
      }

      return {
        startDate,
        cycleDays,
      };
    }

    _getDurationSeconds() {
      const daysAttr = this.getAttribute('data-kik-countdown-duration-days') || '0';
      const hoursAttr = this.getAttribute('data-kik-countdown-duration-hours') || '0';
      const minutesAttr = this.getAttribute('data-kik-countdown-duration-minutes') || '0';
      const secondsAttr = this.getAttribute('data-kik-countdown-duration-seconds') || '0';

      let days = parseInt(daysAttr, 10);
      let hours = parseInt(hoursAttr, 10);
      let minutes = parseInt(minutesAttr, 10);
      let seconds = parseInt(secondsAttr, 10);

      if (Number.isNaN(days)) days = 0;
      if (Number.isNaN(hours)) hours = 0;
      if (Number.isNaN(minutes)) minutes = 0;
      if (Number.isNaN(seconds)) seconds = 0;

      if (days < 0) days = 0;
      if (hours < 0) hours = 0;
      if (minutes < 0) minutes = 0;
      if (seconds < 0) seconds = 0;

      if (minutes > 59) minutes = 59;
      if (seconds > 59) seconds = 59;

      return days * 86400 + hours * 3600 + minutes * 60 + seconds;
    }

    _setTime(days, hours, minutes, seconds) {
      if (this._daysEl) {
        this._daysEl.textContent = kikCountdownPad(days);
      }
      this._hoursEl.textContent = kikCountdownPad(hours);
      this._minutesEl.textContent = kikCountdownPad(minutes);
      this._secondsEl.textContent = kikCountdownPad(seconds);
    }

    _startCountdown() {
      this._clearInterval();

      // Manual hard stop: do not show countdown at all
      const manualEndAttr = this.getAttribute(
        'data-kik-countdown-manual-end'
      );
      const manualEnd =
        manualEndAttr === 'true' ||
        manualEndAttr === '1' ||
        manualEndAttr === 'yes';

      if (manualEnd) {
        this._setTime(0, 0, 0, 0);
        this._hideTarget();
        return;
      }

      // Global hard end time (with timezone support)
      const hardEndAttr = this.getAttribute('data-kik-countdown-hard-end');
      const timezoneOffsetAttr =
        this.getAttribute('data-kik-countdown-timezone-offset') || '+00:00';
      const hardEndDate = kikCountdownParseDateTime(
        hardEndAttr ? hardEndAttr + timezoneOffsetAttr : ''
      );
      if (hardEndDate && Date.now() >= hardEndDate.getTime()) {
        this._setTime(0, 0, 0, 0);
        this._hideTarget();
        return;
      }

      const totalSecondsDuration = this._getDurationSeconds();

      if (totalSecondsDuration <= 0) {
        this._setTime(0, 0, 0, 0);
        this._hideTarget();
        return;
      }

      const startAttr = this.getAttribute('data-kik-countdown-start');
      const { startDate, cycleDays } = this._getCycleConfig();

      // If a start time is provided but cannot be parsed,
      // treat it as invalid and do not show the countdown.
      if (startAttr && !startDate) {
        this._setTime(0, 0, 0, 0);
        this._hideTarget();
        return;
      }

      let effectiveStart;

      // 1）没有配置开始时间：保持原来逻辑，从“现在”开始倒计时一次
      if (!startDate) {
        effectiveStart = new Date();
      } else {
        // 2）配置了开始时间：
        //   - cycleDays <= 0：只按 startDate 跑一次
        //   - cycleDays > 0：根据当前时间推算“当前这一期”的开始时间，但本次只倒计这一期，结束后不自动跳下一期
        if (!cycleDays || cycleDays <= 0) {
          effectiveStart = startDate;
        } else {
          const cycleMs = cycleDays * 24 * 60 * 60 * 1000;
          const startMs = startDate.getTime();
          const nowMs = Date.now();

          if (nowMs <= startMs) {
            effectiveStart = startDate;
          } else {
            const elapsed = nowMs - startMs;
            const cyclesPassed = Math.floor(elapsed / cycleMs);
            const candidateStartMs = startMs + cyclesPassed * cycleMs;
            effectiveStart = new Date(candidateStartMs);
          }
        }
      }

      const endTime = effectiveStart.getTime() + totalSecondsDuration * 1000;

      // 如果当前时间不在本期倒计时区间内，则隐藏目标容器并结束
      const initialNow = Date.now();
      if (initialNow < effectiveStart.getTime() || initialNow >= endTime) {
        this._hideTarget();
        this._setTime(0, 0, 0, 0);
        return;
      }

      // 当前在有效倒计时区间内，确保容器可见
      this._showTarget();

      const update = () => {
        const now = Date.now();
        const diff = endTime - now;

        if (diff <= 0) {
          this._setTime(0, 0, 0, 0);
          this._clearInterval();
          this._hideTarget();
          return;
        }

        const totalSeconds = Math.floor(diff / 1000);
        let days = Math.floor(totalSeconds / 86400);
        let remainingSeconds = totalSeconds % 86400;
        let hours = Math.floor(remainingSeconds / 3600);
        remainingSeconds = remainingSeconds % 3600;
        const minutes = Math.floor(remainingSeconds / 60);
        const seconds = remainingSeconds % 60;

        this._setTime(days, hours, minutes, seconds);
      };

      update();
      this._interval = window.setInterval(update, 1000);
    }
  }

  if (!customElements.get('kik-countdown')) {
    customElements.define('kik-countdown', KikCountdown);
  }

  // Kik wrappers / zoom -----------------------------------

  // Lightweight wrapper to avoid overriding base product-gallery
  (() => {
    const BaseProductGallery = customElements.get('product-gallery') || HTMLElement;
    if (!customElements.get('kik-product-gallery')) {
      class KikProductGallery extends BaseProductGallery {}
      customElements.define('kik-product-gallery', KikProductGallery);
    }
  })();

  const loadSwiperForKik = (() => {
    let promise = null;
    return () => {
      if (window.Swiper) return Promise.resolve();
      if (promise) return promise;
      promise = new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = 'https://cdn.jsdelivr.net/npm/swiper@8/swiper-bundle.min.js';
        script.async = true;
        script.onload = () => resolve();
        script.onerror = () => reject(new Error('kik-gallery-zoom-button: failed to load Swiper'));
        document.head.appendChild(script);
      });
      return promise;
    };
  })();

  class KikGalleryZoomButton extends HTMLElement {
    constructor() {
      super();
      this._onClick = this._onClick.bind(this);
      this._onOverlayClick = this._onOverlayClick.bind(this);
      this._onKeydown = this._onKeydown.bind(this);
    }

    connectedCallback() {
      this._trigger = this.querySelector('button') || this;
      this._trigger.addEventListener('click', this._onClick);
    }

    disconnectedCallback() {
      this._trigger?.removeEventListener('click', this._onClick);
      this._teardownOverlay();
    }

    async _onClick(event) {
      console.log('onClick');
      event?.preventDefault?.();
      event?.stopPropagation?.();
      await this._openOverlay();
    }

    async _openOverlay() {
      const container = this.closest('.kik-gallery-zoom') || this.closest('kik-gallery-zoom') || document;
      const dots = container.querySelector('.product-gallery__thumbnail-list');
      const buttons = Array.from(dots?.querySelectorAll('button:not([hidden])') || []);

      // Collect media items from visible gallery slides to preserve type (image/video)
      const mediaNodes = Array.from(
        container.querySelectorAll('.product-gallery__media:not([hidden])'),
      );

      const items = mediaNodes
        .map((node, index) => {
          const type = node.getAttribute('data-media-type') || 'image';
          const thumbImg = buttons[index]?.querySelector('img');
          if (type === 'image') {
            const img = node.querySelector('img');
            if (!img) return null;
            return {
              type: 'image',
              src: img.currentSrc || img.src,
              srcset: img.srcset,
              sizes: img.sizes,
              alt: img.alt || '',
              thumb: thumbImg,
            };
          }

          if (type === 'video' || type === 'external_video') {
            const videoEl = node.querySelector('video');
            const iframeEl = node.querySelector('iframe');
            let html = '';
            if (videoEl) {
              const clone = videoEl.cloneNode(true);
              clone.removeAttribute('autoplay');
              clone.removeAttribute('loop');
              clone.removeAttribute('muted');
              clone.setAttribute('controls', 'true');
              clone.currentTime = 0;
              html = clone.outerHTML;
            } else if (iframeEl) {
              const clone = iframeEl.cloneNode(true);
              html = clone.outerHTML;
            }
            return {
              type: 'video',
              html,
              thumb: thumbImg,
              alt: thumbImg?.alt || '',
            };
          }
          return null;
        })
        .filter(Boolean);

      if (!items.length) {
        console.warn('kik-gallery-zoom-button: no media found for zoom');
        return;
      }
      if (!window.Swiper) {
        try {
          await loadSwiperForKik();
        } catch (err) {
          console.warn(err?.message || 'kik-gallery-zoom-button: Swiper not available');
          return;
        }
      }

      this._ensureOverlay();
      this._renderSlides(items);
      const initialIndex = Math.max(
        0,
        buttons.findIndex((btn) => btn.getAttribute('aria-current') === 'true'),
      );
      this._initSwipers(initialIndex);
      this._showOverlay();
    }

    _ensureOverlay() {
      if (this._overlay) return;
      const overlay = document.createElement('div');
      overlay.className = 'kik-zoom-overlay';
      overlay.innerHTML = `
        <div class="kik-zoom" role="dialog" aria-modal="true" aria-label="Gallery">
          <button type="button" class="kik-zoom__close" aria-label="Close gallery"><span aria-hidden="true">&times;</span></button>
          <div class="kik-zoom__stage">
            <button type="button" class="kik-zoom__arrow kik-zoom__arrow--prev" aria-label="Previous">
              <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                <path d="M15 5 8 12l7 7" stroke="currentColor" stroke-width="2" fill="none" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </button>
            <div class="swiper kik-zoom__swiper">
              <div class="swiper-wrapper"></div>
            </div>
            <button type="button" class="kik-zoom__arrow kik-zoom__arrow--next" aria-label="Next">
              <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                <path d="m9 5 7 7-7 7" stroke="currentColor" stroke-width="2" fill="none" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </button>
          </div>
          <div class="swiper kik-zoom__thumbs">
            <div class="swiper-wrapper"></div>
          </div>
        </div>
      `;

      overlay.addEventListener('click', this._onOverlayClick);
      document.body.appendChild(overlay);

      this._overlay = overlay;
      this._mainWrapper = overlay.querySelector('.kik-zoom__swiper .swiper-wrapper');
      this._thumbWrapper = overlay.querySelector('.kik-zoom__thumbs .swiper-wrapper');
      this._closeBtn = overlay.querySelector('.kik-zoom__close');
      this._prevBtn = overlay.querySelector('.kik-zoom__arrow--prev');
      this._nextBtn = overlay.querySelector('.kik-zoom__arrow--next');

      this._closeBtn?.addEventListener('click', () => this._hideOverlay());
    }

    _renderSlides(items) {
      if (!this._mainWrapper || !this._thumbWrapper) return;
      this._mainWrapper.innerHTML = '';
      this._thumbWrapper.innerHTML = '';

      items.forEach((item) => {
        const main = document.createElement('div');
        main.className = 'swiper-slide';
        if (item.type === 'video' && item.html) {
          main.innerHTML = `<div class="kik-zoom__video">${item.html}</div>`;
        } else {
          main.innerHTML = `<img src="${item.src}" ${item.srcset ? `srcset="${item.srcset}"` : ''} ${
            item.sizes ? `sizes="${item.sizes}"` : ''
          } alt="${this._escape(item.alt)}" />`;
        }
        this._mainWrapper.appendChild(main);

        const thumb = document.createElement('div');
        thumb.className = 'swiper-slide';
        const thumbImg = item.thumb;
        const thumbSrc = thumbImg?.currentSrc || thumbImg?.src || item.src || '';
        const thumbAlt = thumbImg?.alt || item.alt || '';
        const badge = item.type === 'video' ? '<span class="kik-zoom__thumb-badge" aria-hidden="true">▶</span>' : '';
        thumb.innerHTML = `<img src="${thumbSrc}" alt="${this._escape(thumbAlt)}" />${badge}`;
        this._thumbWrapper.appendChild(thumb);
      });
    }

    _initSwipers(initialIndex) {
      this._thumbSwiper?.destroy(true, true);
      this._mainSwiper?.destroy(true, true);

      this._thumbSwiper = new window.Swiper(this._thumbWrapper.parentElement, {
        spaceBetween: 5,
        slidesPerView: 'auto',
        freeMode: false,
        watchSlidesProgress: true,
        slideToClickedSlide: true,
        centeredSlides: false,
      });

      this._mainSwiper = new window.Swiper(this._mainWrapper.parentElement, {
        spaceBetween: 10,
        slidesPerView: 1,
        initialSlide: initialIndex,
        navigation: {
          nextEl: this._nextBtn,
          prevEl: this._prevBtn,
        },
        thumbs: {
          swiper: this._thumbSwiper,
        },
        allowTouchMove: true,
        on: {
          afterInit: (swiper) => {
            // ensure navigation buttons are bound even if rendered outside Swiper
            swiper.navigation?.update();
            this._scrollThumbIntoView(swiper.activeIndex);
          },
          slideChange: (swiper) => {
            this._scrollThumbIntoView(swiper.activeIndex);
          },
        },
      });

      const multiple = (this._mainSwiper.slides?.length || 0) > 1;
      this._prevBtn?.toggleAttribute('hidden', !multiple);
      this._nextBtn?.toggleAttribute('hidden', !multiple);
    }

    _showOverlay() {
      if (!this._overlay) return;
      this._overlay.classList.add('is-visible');
      document.body.classList.add('kik-zoom-open');
      document.documentElement.classList.add('kik-zoom-open');
      document.addEventListener('keydown', this._onKeydown);
    }

    _hideOverlay() {
      if (!this._overlay) return;
      this._overlay.classList.remove('is-visible');
      document.body.classList.remove('kik-zoom-open');
      document.documentElement.classList.remove('kik-zoom-open');
      document.removeEventListener('keydown', this._onKeydown);
    }

    _teardownOverlay() {
      document.removeEventListener('keydown', this._onKeydown);
      if (this._overlay?.parentElement) {
        this._overlay.removeEventListener('click', this._onOverlayClick);
        this._overlay.parentElement.removeChild(this._overlay);
      }
      this._mainSwiper?.destroy(true, true);
      this._thumbSwiper?.destroy(true, true);
      this._overlay = null;
      this._mainWrapper = null;
      this._thumbWrapper = null;
      this._mainSwiper = null;
      this._thumbSwiper = null;
    }

    _onOverlayClick(event) {
      if (event.target === this._overlay) {
        this._hideOverlay();
      }
    }

    _onKeydown(event) {
      if (!this._overlay?.classList.contains('is-visible')) return;
      if (event.key === 'Escape') {
        this._hideOverlay();
      } else if (event.key === 'ArrowRight') {
        this._mainSwiper?.slideNext();
      } else if (event.key === 'ArrowLeft') {
        this._mainSwiper?.slidePrev();
      }
    }

    _escape(value) {
      return String(value || '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
    }

    _scrollThumbIntoView(index) {
      if (!this._thumbSwiper) return;
      const slides = this._thumbSwiper.slides || [];
      const slide = slides[index];
      if (!slide) return;

      const slideWidth = slide.clientWidth || 80;
      const visibleCount = Math.max(1, Math.floor(this._thumbSwiper.width / slideWidth));
      const target = Math.max(
        0,
        Math.min(index - Math.floor((visibleCount - 1) / 2), slides.length - visibleCount),
      );

      this._thumbSwiper.slideTo(target, 300, false);
    }
  }

  if (!customElements.get('kik-gallery-zoom-button')) {
    customElements.define('kik-gallery-zoom-button', KikGalleryZoomButton);
  }

  // Kik Product Variants Compare -----------------------------------
  class KikProductVariantsCompare extends HTMLElement {
    constructor() {
      super();
      this.initialized = false;
      this.variantData = null;
      this.currentVariant = null;
      this.productOptions = null;
      this.sizeIndex = -1;
      this.thicknessIndex = -1;
      this.variantImageMap = {};
      this.mainProductModal = null;
      this.variantContentMap = {};
      
      // Slider properties
      this.currentSlide = 0;
      this.isDragging = false;
      this.startX = 0;
      this.currentX = 0;
      this.sliderContainer = null;
      this.cards = [];
      this.dots = [];
    }

    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      // Initialize variant data from data attributes
      this.initializeVariantData();

      // Get all variant cards
      const compareCards = this.querySelectorAll('.variant-compare-card');
      if (compareCards.length === 0) {
        console.warn('No variant comparison cards found');
        return;
      }

      // Get indices from first card
      const firstCard = compareCards[0];
      this.sizeIndex = parseInt(firstCard.dataset.sizeOptionIndex) || -1;
      this.thicknessIndex = parseInt(firstCard.dataset.thicknessOptionIndex) || -1;

      // Initialize cards
      this.initializeCards();

      // Watch for variant changes
      this.watchForVariantChanges();

      // Add event listeners
      this.addEventListener('click', this.handleClick.bind(this));

      // Initialize info button modal handling
      this.initializeInfoModal();
      
      // Initialize slider
      this.initializeSlider();
    }

    getCurrentSize() {
      const variantInput = document.querySelector('input[name="id"]');
      if (variantInput && variantInput.value && this.variantData) {
        const variant = this.variantData.find(v => v.id === parseInt(variantInput.value));
        if (variant && this.sizeIndex >= 0) {
          return variant[`option${this.sizeIndex}`];
        }
      }
      
      if (this.currentVariant && this.sizeIndex >= 0) {
        return this.currentVariant[`option${this.sizeIndex}`];
      }
      
      return null;
    }

    getCurrentThickness() {
      const variantInput = document.querySelector('input[name="id"]');
      if (variantInput && variantInput.value && this.variantData) {
        const variant = this.variantData.find(v => v.id === parseInt(variantInput.value));
        if (variant && this.thicknessIndex >= 0) {
          return variant[`option${this.thicknessIndex}`];
        }
      }
      
      if (this.currentVariant && this.thicknessIndex >= 0) {
        return this.currentVariant[`option${this.thicknessIndex}`];
      }
      
      return null;
    }

    getVariantBySizeAndThickness(size, thickness) {
      if (!this.variantData) return null;
      
      return this.variantData.find(v => {
        const variantSize = this.sizeIndex >= 0 ? v[`option${this.sizeIndex}`] : null;
        const variantThickness = this.thicknessIndex >= 0 ? v[`option${this.thicknessIndex}`] : null;
        return variantSize === size && variantThickness === thickness;
      });
    }

    updateStarRating(card, rating) {
      const starsContainer = card.querySelector('.variant-stars');
      if (!starsContainer) return;
      
      const ratingValue = parseFloat(rating) || 0;
      const fullStars = Math.floor(ratingValue);
      const decimalPart = ratingValue - fullStars;
      
      // Clear existing stars
      const existingStars = starsContainer.querySelectorAll('svg');
      existingStars.forEach((star, index) => {
        if (index < fullStars) {
          // Full star
          star.querySelector('path').setAttribute('fill', '#05305d');
        } else if (index === fullStars && decimalPart > 0) {
          // Partial star - update the clipPath width
          const clipRect = star.querySelector('clipPath rect');
          if (clipRect) {
            const newWidth = decimalPart * 64; // 64 is the viewBox width
            clipRect.setAttribute('width', newWidth.toString());
          }
        } else {
          // Empty star
          const path = star.querySelector('path:last-child');
          if (path) {
            path.setAttribute('fill', 'none');
          }
        }
      });
    }

    updateCards(selectedSize, selectedThickness) {
      if (!selectedSize) {
        console.warn('No size selected');
        return;
      }
      
      const compareCards = this.querySelectorAll('.variant-compare-card');
      
      compareCards.forEach(card => {
        const thickness = card.dataset.thickness;
        const variant = this.getVariantBySizeAndThickness(selectedSize, thickness);
        
        // Update size label
        const sizeLabel = card.querySelector('[data-size-label]');
        if (sizeLabel) {
          // Capitalize first letter only (e.g., "Queen" not "QUEEN")
          const capitalizedSize = selectedSize.charAt(0).toUpperCase() + selectedSize.slice(1).toLowerCase();
          sizeLabel.textContent = capitalizedSize + ':';
        }
        
        // Highlight current viewing card
        if (thickness === selectedThickness) {
          card.classList.add('is-current-viewing');
        } else {
          card.classList.remove('is-current-viewing');
        }
        
        if (variant) {
          // Update variant image
          const variantImage = card.querySelector('.variant-image-wrapper img');
          if (variantImage && window.variantImageMap) {
            const newImageUrl = window.variantImageMap[variant.id];
            if (newImageUrl) {
              variantImage.src = newImageUrl;
            }
          }
          
          // Update price
          const priceElement = card.querySelector('[data-variant-price]');
          if (priceElement) {
            const price = (variant.price / 100).toFixed(2);
            priceElement.textContent = '$' + price;
          }
          
          // Update total value
          const totalValueElement = card.querySelector('[data-total-value]');
          if (totalValueElement && variant.compare_at_price) {
            const comparePrice = (variant.compare_at_price / 100).toFixed(2);
            totalValueElement.textContent = '$' + comparePrice;
          }
          
          // Update button
          const addToCartBtn = card.querySelector('[data-add-to-cart]');
          if (addToCartBtn) {
            addToCartBtn.dataset.variantId = variant.id;
            addToCartBtn.disabled = !variant.available;
            const originalText = addToCartBtn.dataset.originalText || 'Add To Cart';
            addToCartBtn.textContent = variant.available ? originalText : 'Sold Out';
          }

          // Update dynamic content from variant content map
          const content = this.variantContentMap[variant.id];
          if (content) {
            console.log(`Updating content for variant ${variant.id}:`, content);
            
            // Update badge
            const badge = card.querySelector('.variant-badge');
            if (badge && content.badgeText) {
              badge.textContent = content.badgeText;
              badge.style.display = content.badgeText ? 'block' : 'none';
            }
            
            // Update title
            const title = card.querySelector('.variant-content h3');
            if (title && content.title) {
              title.textContent = content.title;
              console.log(`Updated title for variant ${variant.id} to: ${content.title}`);
            }
            
            // Update description
            const variantContent = card.querySelector('.variant-content');
            if (variantContent && content.description && !content.description.error) {
              // Find existing description element or metafield-tag
              let descElement = variantContent.querySelector('metafield-tag');
              if (!descElement) {
                // If no metafield-tag, look for any element after h3
                const h3 = variantContent.querySelector('h3');
                if (h3 && h3.nextElementSibling) {
                  descElement = h3.nextElementSibling;
                }
              }
              if (descElement) {
                const descText = typeof content.description === 'object' ? '' : content.description;
                descElement.innerHTML = descText;
              }
            }
            
            // Update reviews count - use defaults if needed
            const reviewsCount = card.querySelector('.variant-reviews-count');
            if (reviewsCount) {
              const count = (content.reviewsCount && content.reviewsCount > 0) ? content.reviewsCount : 613;
              reviewsCount.textContent = `${count} Reviews`;
              // Always show reviews section since we have defaults
              const reviewsSection = card.querySelector('.variant-reviews');
              if (reviewsSection) {
                reviewsSection.style.display = 'flex';
              }
            }
            
            // Update star rating - use default of 5 if error or 0
            const starContainer = card.querySelector('.variant-stars');
            if (starContainer) {
              let rating = 5; // Default
              if (content.starRating !== undefined && !content.starRating.error && typeof content.starRating !== 'object') {
                rating = content.starRating || 5;
              }
              starContainer.setAttribute('data-rating', rating);
              // Trigger star rating update
              this.updateStarRating(card, rating);
            }
            
            // Update testimonial
            const testimonialText = card.querySelector('.testimonial-text');
            if (testimonialText && content.testimonialText && !content.testimonialText.error) {
              const testText = typeof content.testimonialText === 'object' ? '' : content.testimonialText;
              if (testText) {
                testimonialText.textContent = `"${testText}"`;
              }
            }
            
            const testimonialAuthor = card.querySelector('.testimonial-author');
            if (testimonialAuthor && content.testimonialAuthor) {
              testimonialAuthor.textContent = content.testimonialAuthor;
            }
            
            const testimonialAvatar = card.querySelector('.testimonial-avatar');
            if (testimonialAvatar && content.testimonialAvatar) {
              testimonialAvatar.src = content.testimonialAvatar;
              console.log(`Updated avatar for variant ${variant.id} to: ${content.testimonialAvatar}`);
            }
            
            // Update testimonial tags
            if (content.testimonialTags) {
              const tagsContainer = card.querySelector('.testimonial-tags');
              if (tagsContainer) {
                const tags = content.testimonialTags.split(',').map(t => t.trim()).filter(t => t);
                if (tags.length > 0) {
                  tagsContainer.innerHTML = tags.map(tag => `
                    <span class="testimonial-tag">
                      <svg width="16px" height="16px" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <g id="SVGRepo_bgCarrier" stroke-width="0"></g>
                        <g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round" stroke="#CCCCCC" stroke-width="0.144"></g>
                        <g id="SVGRepo_iconCarrier">
                          <path fill-rule="evenodd" clip-rule="evenodd" d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22ZM10.2426 14.4142L17.3137 7.34315L18.7279 8.75736L10.2426 17.2426L6 13L7.41421 11.5858L10.2426 14.4142Z" fill="rgba(5, 48, 93, 1)"></path>
                        </g>
                      </svg>
                      ${tag}
                    </span>
                  `).join('');
                } else {
                  tagsContainer.innerHTML = '';
                }
              }
            }
          }
          
          card.style.display = 'flex';
        } else {
          card.style.display = 'none';
        }
      });
      
      // Update slider after cards visibility changes
      if (this.sliderContainer) {
        setTimeout(() => {
          this.handleResize();
          this.updateArrowButtons();
          this.equalizeCardHeights();
        }, 100);
      }
    }

    handleClick(event) {
      if (event.target.closest('[data-add-to-cart]')) {
        event.preventDefault();
        this.handleAddToCart(event.target.closest('[data-add-to-cart]'));
      }
    }

    async handleAddToCart(button) {
      const variantId = button.dataset.variantId;
      
      if (!variantId || button.disabled) return;

      const originalText = button.dataset.originalText || button.textContent;
      button.textContent = 'Adding...';
      button.disabled = true;

      try {
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: variantId, quantity: 1 })
        });

        await response.json();
        document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
        button.textContent = 'Added!';
        
        setTimeout(() => {
          window.location.href = '/cart';
        }, 500);
      } catch (error) {
        console.error('Error adding to cart:', error);
        button.textContent = originalText;
        button.disabled = false;
      }
    }

    initializeCards() {
      // Initial update with delay to ensure variant picker is ready
      setTimeout(() => {
        const currentSize = this.getCurrentSize();
        const currentThickness = this.getCurrentThickness();
        if (currentSize) {
          this.updateCards(currentSize, currentThickness);
        }
      }, 300);
    }

    watchForVariantChanges() {
      // Watch for variant input changes
      const variantInput = document.querySelector('input[name="id"]');
      if (variantInput) {
        const observer = new MutationObserver(() => {
          setTimeout(() => {
            const newSize = this.getCurrentSize();
            const newThickness = this.getCurrentThickness();
            if (newSize) {
              this.updateCards(newSize, newThickness);
            }
          }, 100);
        });
        observer.observe(variantInput, { attributes: true, attributeFilter: ['value'] });
      }
      
      // Listen for option changes
      document.addEventListener('change', (e) => {
        if (e.target.name && e.target.name.toLowerCase().includes('option')) {
          setTimeout(() => {
            const newSize = this.getCurrentSize();
            const newThickness = this.getCurrentThickness();
            if (newSize) {
              this.updateCards(newSize, newThickness);
            }
          }, 100);
        }
      });
    }

    initializeVariantData() {
      // Get variant data from data attributes
      const variantDataAttr = this.dataset.variants;
      const currentVariantAttr = this.dataset.currentVariant;
      const productOptionsAttr = this.dataset.productOptions;

      if (variantDataAttr) {
        try {
          this.variantData = JSON.parse(variantDataAttr);
          window.variantData = this.variantData; // Keep window variable for backward compatibility
        } catch (e) {
          console.error('Error parsing variant data:', e);
        }
      }

      if (currentVariantAttr) {
        try {
          this.currentVariant = JSON.parse(currentVariantAttr);
          window.currentVariant = this.currentVariant; // Keep window variable for backward compatibility
        } catch (e) {
          console.error('Error parsing current variant:', e);
        }
      }

      if (productOptionsAttr) {
        try {
          this.productOptions = JSON.parse(productOptionsAttr);
          window.productOptions = this.productOptions; // Keep window variable for backward compatibility
        } catch (e) {
          console.error('Error parsing product options:', e);
        }
      }

      // Get variant image map from data attribute if provided (this has priority mappings from Liquid)
      const variantImageMapAttr = this.dataset.variantImageMap;
      if (variantImageMapAttr) {
        try {
          const parsedMap = JSON.parse(variantImageMapAttr);
          this.variantImageMap = parsedMap;
          window.variantImageMap = this.variantImageMap;
        } catch (e) {
          console.error('Error parsing variant image map:', e);
        }
      }

      // If no image map from data attribute, build from variant data
      if (Object.keys(this.variantImageMap).length === 0 && this.variantData) {
        this.variantData.forEach(variant => {
          const imgUrl = variant.featured_media ? variant.featured_media.preview_image.src : null;
          if (imgUrl) {
            this.variantImageMap[variant.id] = imgUrl;
          }
        });
        window.variantImageMap = this.variantImageMap; // Keep window variable for backward compatibility
      }

      // Get variant content map from data attribute
      const variantContentMapAttr = this.dataset.variantContentMap;
      if (variantContentMapAttr) {
        try {
          const parsedMap = JSON.parse(variantContentMapAttr);
          this.variantContentMap = parsedMap;
          window.variantContentMap = this.variantContentMap; // Keep window variable for backward compatibility
          console.log('Loaded variant content map:', this.variantContentMap);
        } catch (e) {
          console.error('Error parsing variant content map:', e);
        }
      }
    }

    initializeInfoModal() {
      const infoButtons = this.querySelectorAll('[data-variant-compare-info]');
      
      // Try multiple ways to find the modal
      let mainProductModal = document.querySelector('dialog-element.kiks-variant-modal');
      console.log('Looking for modal...', mainProductModal);
      
      // If not found, wait a bit as it might be loaded asynchronously
      if (!mainProductModal) {
        setTimeout(() => {
          mainProductModal = document.querySelector('dialog-element.kiks-variant-modal');
          console.log('Second attempt to find modal...', mainProductModal);
          this.setupModal(infoButtons, mainProductModal);
        }, 1000);
      } else {
        this.setupModal(infoButtons, mainProductModal);
      }
    }
    
    setupModal(infoButtons, mainProductModal) {
      if (mainProductModal) {
        console.log('Modal found with ID:', mainProductModal.id);
        
        // Check if modal body is empty and add default content if needed
        const modalBody = mainProductModal.querySelector('.kiks-variant-modal__body');
        const modalText = mainProductModal.querySelector('.kiks-variant-modal__text');
        
        if (modalBody && (!modalText || modalText.textContent.trim() === '')) {
          console.log('Adding default content to empty modal');
          modalBody.innerHTML = `
            <div class="kiks-variant-modal__text">
              <h3 style="font-size: 2rem; margin-bottom: 1.5rem; font-weight: 700;">The best products<br>for the best price</h3>
              <p style="margin-bottom: 1rem;">We NEVER overcharge you for great products! Our percent off claims are not discounts from regular prices we charge, they are REAL SAVINGS you get when you buy our products versus the competition.</p>
              <p style="margin-bottom: 2rem;">Our mattress design team scours the market to find the best performing premium materials at the best value for you. We regularly establish a Total Value for each product we sell and show you the price of our products as a savings against this value.</p>
              <h4 style="font-size: 1.2rem; margin-bottom: 1rem; font-weight: 600;">What is Total Value?</h4>
              <p>We identify three comparably built products and periodically track the actual prices they are sold for not the price "they" claim they are worth. We establish our Total Value at or below the average of the selected products. To find how Total Value is calculated you can learn more here.</p>
            </div>
          `;
        }
        
        // Check if footer is empty and add default buttons if needed
        const modalFooter = mainProductModal.querySelector('.kiks-variant-modal__footer');
        if (modalFooter && modalFooter.children.length === 0) {
          console.log('Adding default buttons to empty footer');
          modalFooter.innerHTML = `
            <a href="/collections/all" class="kiks-variant-modal__button kiks-variant-modal__button--primary">
              Shop Now
            </a>
            <a href="/pages/about-us" class="kiks-variant-modal__button kiks-variant-modal__button--secondary">
              Learn More
            </a>
          `;
        }
        
        // Update all info buttons with the correct modal ID
        infoButtons.forEach(button => {
          button.setAttribute('aria-controls', mainProductModal.id);
        });
      } else {
        console.log('Modal not found! Creating a simple fallback modal...');
        
        // Create a simple fallback modal if none exists
        const fallbackModal = document.createElement('dialog-element');
        fallbackModal.id = 'variant-compare-modal-fallback';
        fallbackModal.className = 'kiks-variant-modal';
        fallbackModal.setAttribute('handle-section-events', '');
        fallbackModal.innerHTML = `
          <div class="kiks-variant-modal__content">
            <div class="kiks-variant-modal__header">
              <h2 class="kiks-variant-modal__title">OUR PRICING</h2>
              <button is="close-button" class="kiks-variant-modal__close" aria-label="Close">
                <svg class="icon icon--close" role="presentation" viewBox="0 0 16 17">
                  <path d="M.865 15.978a.5.5 0 00.707.707l7.433-7.431 7.579 7.282a.501.501 0 00.846-.37.5.5 0 00-.153-.351L9.712 8.546l7.417-7.416a.5.5 0 10-.707-.708L8.991 7.853 1.413.573a.5.5 0 10-.693.72l7.563 7.268-7.418 7.417z" fill="currentColor">
                </svg>
              </button>
            </div>
            <div class="kiks-variant-modal__body">
              <div class="kiks-variant-modal__text">
                <h3 style="font-size: 2rem; margin-bottom: 1.5rem; font-weight: 700;">The best products<br>for the best price</h3>
                <p style="margin-bottom: 1rem;">We NEVER overcharge you for great products! Our percent off claims are not discounts from regular prices we charge, they are REAL SAVINGS you get when you buy our products versus the competition.</p>
                <p style="margin-bottom: 2rem;">Our mattress design team scours the market to find the best performing premium materials at the best value for you. We regularly establish a Total Value for each product we sell and show you the price of our products as a savings against this value.</p>
                <h4 style="font-size: 1.2rem; margin-bottom: 1rem; font-weight: 600;">What is Total Value?</h4>
                <p>We identify three comparably built products and periodically track the actual prices they are sold for not the price "they" claim they are worth. We establish our Total Value at or below the average of the selected products. To find how Total Value is calculated you can learn more here.</p>
              </div>
            </div>
            <div class="kiks-variant-modal__footer">
              <a href="/collections/all" class="kiks-variant-modal__button kiks-variant-modal__button--primary">
                Shop Now
              </a>
              <a href="/pages/about-us" class="kiks-variant-modal__button kiks-variant-modal__button--secondary">
                Learn More
              </a>
            </div>
          </div>
        `;
        
        // Add the fallback modal to the body
        document.body.appendChild(fallbackModal);
        
        // Set up the buttons to use the fallback modal
        infoButtons.forEach(button => {
          button.setAttribute('aria-controls', 'variant-compare-modal-fallback');
        });
      }
      
      // Simplified click handler - just find and click the original button
      infoButtons.forEach(button => {
        button.addEventListener('click', (e) => {
          e.preventDefault();
          
          // Find the original info button from main product section
          const originalInfoBtn = document.querySelector('[data-kiks-size-info]');
          if (originalInfoBtn) {
            // Simply click it - let the original handler do its work
            originalInfoBtn.click();
          } else {
            console.error('Original info button not found. Make sure the main product section has the info icon enabled.');
          }
        });
      });
    }
    
    initializeSlider() {
      this.sliderContainer = this.querySelector('.variants-compare-grid');
      if (!this.sliderContainer) {
        console.warn('Slider container not found');
        return;
      }
      
      this.cards = Array.from(this.sliderContainer.querySelectorAll('.variant-compare-card'));
      this.dots = Array.from(this.querySelectorAll('.pagination-dot'));
      
      console.log('Slider initialized with', this.cards.length, 'cards and', this.dots.length, 'dots');
      
      // Initialize touch/mouse events
      this.sliderContainer.addEventListener('touchstart', this.handleTouchStart.bind(this), { passive: true });
      this.sliderContainer.addEventListener('touchmove', this.handleTouchMove.bind(this), { passive: true });
      this.sliderContainer.addEventListener('touchend', this.handleTouchEnd.bind(this));
      
      this.sliderContainer.addEventListener('mousedown', this.handleMouseDown.bind(this));
      this.sliderContainer.addEventListener('mousemove', this.handleMouseMove.bind(this));
      this.sliderContainer.addEventListener('mouseup', this.handleMouseUp.bind(this));
      this.sliderContainer.addEventListener('mouseleave', this.handleMouseUp.bind(this));
      
      // Initialize pagination dots
      this.dots.forEach((dot, index) => {
        dot.addEventListener('click', () => {
          console.log('Dot clicked:', index);
          this.goToSlide(index);
        });
      });
      
      // Initialize arrow navigation
      const prevButton = this.querySelector('.variants-slider-arrow--prev');
      const nextButton = this.querySelector('.variants-slider-arrow--next');
      
      if (prevButton) {
        prevButton.addEventListener('click', () => {
          console.log('Previous button clicked');
          this.navigatePrev();
        });
      }
      
      if (nextButton) {
        nextButton.addEventListener('click', () => {
          console.log('Next button clicked');
          this.navigateNext();
        });
      }
      
      // Handle resize
      window.addEventListener('resize', this.handleResize.bind(this));
      
      // Set initial position after a delay to ensure cards are rendered
      setTimeout(() => {
        this.updateSliderPosition();
        this.updatePaginationDots();
        this.updateArrowButtons();
        this.equalizeCardHeights();
      }, 100);
    }
    
    getCardsPerView() {
      const width = window.innerWidth;
      if (width <= 768) return 1;
      if (width <= 1024) return 2;
      return 3;
    }
    
    goToSlide(index) {
      const visibleCards = this.cards.filter(card => card.style.display !== 'none');
      const cardsPerView = this.getCardsPerView();
      const maxSlide = Math.max(0, visibleCards.length - cardsPerView);
      
      this.currentSlide = Math.max(0, Math.min(index, maxSlide));
      this.updateSliderPosition();
      this.updatePaginationDots();
      this.updateArrowButtons();
    }
    
    updateSliderPosition() {
      const visibleCards = this.cards.filter(card => card.style.display !== 'none');
      if (!visibleCards.length) {
        console.log('No visible cards found');
        return;
      }
      
      // Get container width to calculate card width properly
      const containerWidth = this.sliderContainer.parentElement.offsetWidth;
      const cardsPerView = this.getCardsPerView();
      const gap = 20; // Fixed gap value
      
      // Calculate card width based on container and cards per view
      const totalGaps = (cardsPerView - 1) * gap;
      const cardWidth = (containerWidth - totalGaps) / cardsPerView;
      
      // Set card widths explicitly without affecting height
      visibleCards.forEach(card => {
        card.style.width = `${cardWidth}px`;
        card.style.minWidth = `${cardWidth}px`;
        card.style.maxWidth = `${cardWidth}px`;
        card.style.flex = `0 0 ${cardWidth}px`;
        // Ensure height remains flexible
        card.style.height = '';
        card.style.minHeight = '';
        card.style.maxHeight = '';
      });
      
      const translateX = -(this.currentSlide * (cardWidth + gap));
      
      console.log('Updating slider position:', { 
        visibleCards: visibleCards.length,
        containerWidth,
        cardsPerView,
        cardWidth, 
        gap, 
        currentSlide: this.currentSlide,
        translateX 
      });
      
      this.sliderContainer.style.transform = `translateX(${translateX}px)`;
      
      // Ensure all cards have equal height
      this.equalizeCardHeights();
    }
    
    equalizeCardHeights() {
      const visibleCards = this.cards.filter(card => card.style.display !== 'none');
      if (visibleCards.length === 0) return;
      
      // Reset heights to allow natural height calculation
      visibleCards.forEach(card => {
        card.style.height = '';
      });
      
      // Wait for layout to settle
      setTimeout(() => {
        // Find the maximum natural height
        let maxHeight = 0;
        visibleCards.forEach(card => {
          const height = card.offsetHeight;
          if (height > maxHeight) {
            maxHeight = height;
          }
        });
        
        // Set all cards to the maximum height
        if (maxHeight > 0) {
          visibleCards.forEach(card => {
            card.style.height = `${maxHeight}px`;
          });
        }
      }, 10);
    }
    
    updatePaginationDots() {
      this.dots.forEach((dot, index) => {
        dot.classList.toggle('active', index === this.currentSlide);
      });
    }
    
    navigatePrev() {
      const visibleCards = this.cards.filter(card => card.style.display !== 'none');
      const cardsPerView = this.getCardsPerView();
      const maxSlide = Math.max(0, visibleCards.length - cardsPerView);
      
      if (this.currentSlide > 0) {
        this.currentSlide--;
        this.updateSliderPosition();
        this.updatePaginationDots();
        this.updateArrowButtons();
      }
    }
    
    navigateNext() {
      const visibleCards = this.cards.filter(card => card.style.display !== 'none');
      const cardsPerView = this.getCardsPerView();
      const maxSlide = Math.max(0, visibleCards.length - cardsPerView);
      
      if (this.currentSlide < maxSlide) {
        this.currentSlide++;
        this.updateSliderPosition();
        this.updatePaginationDots();
        this.updateArrowButtons();
      }
    }
    
    updateArrowButtons() {
      const prevButton = this.querySelector('.variants-slider-arrow--prev');
      const nextButton = this.querySelector('.variants-slider-arrow--next');
      const visibleCards = this.cards.filter(card => card.style.display !== 'none');
      const cardsPerView = this.getCardsPerView();
      const maxSlide = Math.max(0, visibleCards.length - cardsPerView);
      
      if (prevButton) {
        prevButton.disabled = this.currentSlide === 0;
      }
      
      if (nextButton) {
        nextButton.disabled = this.currentSlide >= maxSlide;
      }
    }
    
    handleTouchStart(e) {
      this.startX = e.touches[0].clientX;
      this.isDragging = true;
      this.sliderContainer.classList.add('dragging');
    }
    
    handleTouchMove(e) {
      if (!this.isDragging) return;
      this.currentX = e.touches[0].clientX;
    }
    
    handleTouchEnd() {
      if (!this.isDragging) return;
      this.handleDragEnd();
    }
    
    handleMouseDown(e) {
      e.preventDefault();
      this.startX = e.clientX;
      this.isDragging = true;
      this.sliderContainer.classList.add('dragging');
    }
    
    handleMouseMove(e) {
      if (!this.isDragging) return;
      this.currentX = e.clientX;
    }
    
    handleMouseUp() {
      if (!this.isDragging) return;
      this.handleDragEnd();
    }
    
    handleDragEnd() {
      this.isDragging = false;
      this.sliderContainer.classList.remove('dragging');
      
      const dragDistance = this.currentX - this.startX;
      const threshold = 100; // pixels
      
      const visibleCards = this.cards.filter(card => card.style.display !== 'none');
      const cardsPerView = this.getCardsPerView();
      const maxSlide = Math.max(0, visibleCards.length - cardsPerView);
      
      if (dragDistance > threshold && this.currentSlide > 0) {
        // Swipe right
        this.currentSlide--;
      } else if (dragDistance < -threshold && this.currentSlide < maxSlide) {
        // Swipe left
        this.currentSlide++;
      }
      
      this.updateSliderPosition();
      this.updatePaginationDots();
      this.updateArrowButtons();
    }
    
    handleResize() {
      // Recalculate position on resize
      const visibleCards = this.cards.filter(card => card.style.display !== 'none');
      const cardsPerView = this.getCardsPerView();
      const maxSlide = Math.max(0, visibleCards.length - cardsPerView);
      
      if (this.currentSlide > maxSlide) {
        this.currentSlide = maxSlide;
      }
      
      this.updateSliderPosition();
      this.updatePaginationDots();
      this.updateArrowButtons();
      
      // Also equalize heights on resize
      this.equalizeCardHeights();
    }
  }

  if (!customElements.get('kik-product-variants-compare')) {
    customElements.define('kik-product-variants-compare', KikProductVariantsCompare);
  }

  /* ================================================================
   * KikProductCompare — Collection page product comparison feature
   * ================================================================ */
  class KikProductCompare {
    constructor() {
      this.maxProducts = 5;
      this.compareProducts = [];
      this.isCompareMode = false;
      this.isBarExpanded = false;
      this.init();
    }

    init() {
      this.toggle = document.querySelector('[data-compare-toggle]');
      this.bar = document.querySelector('[data-compare-bar]');
      this.modal = document.querySelector('[data-compare-modal]');
      this.countEl = document.querySelector('[data-compare-count]');
      this.thumbnailsEl = document.querySelector('[data-compare-thumbnails]');
      this.thumbnailsMobileEl = document.querySelector('[data-compare-thumbnails-mobile]');
      this.modalBody = document.querySelector('[data-compare-modal-body]');
      this.tabFiltersContainer = document.querySelector('[data-tab-filters]');
      this.progressEl = document.querySelector('[data-compare-progress]');
      this.chevronBtn = document.querySelector('[data-compare-bar-toggle]');

      if (!this.toggle || !this.bar || !this.modal) return;

      /* Compare toggle */
      this.toggle.addEventListener('change', () => this.toggleCompareMode());

      /* Checkbox changes on product cards */
      document.addEventListener('change', (e) => {
        if (e.target.matches('[data-compare-input]')) {
          this.handleCheckboxChange(e.target);
        }
      });

      /* Sticky bar buttons */
      const openModalBtn = this.bar.querySelector('[data-compare-open-modal]');
      if (openModalBtn) openModalBtn.addEventListener('click', () => this.openModal());

      const clearBtns = this.bar.querySelectorAll('[data-compare-clear]');
      clearBtns.forEach((btn) => btn.addEventListener('click', (e) => { e.preventDefault(); this.clearAll(); }));

      /* Mobile chevron expand/collapse */
      if (this.chevronBtn) {
        this.chevronBtn.addEventListener('click', () => this.toggleBarExpand());
      }

      /* Modal close buttons */
      this.modal.querySelectorAll('[data-compare-modal-close]').forEach((btn) => {
        btn.addEventListener('click', () => this.closeModal());
      });

      /* Keyboard: Escape closes modal */
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.modal.style.display !== 'none') {
          this.closeModal();
        }
      });

      /* Initialize tab filters */
      this.initTabFilters();

      /* Move checkboxes into product card image containers for proper positioning */
      this.repositionCheckboxes();

      /* Restore state from sessionStorage */
      this.restoreState();
    }

    /* ---------- Tab Filters ---------- */
    initTabFilters() {
      if (!this.tabFiltersContainer) return;
      const cards = document.querySelectorAll('[data-compare-product]');
      const tabLabels = new Set();
      cards.forEach((card) => {
        const label = (card.dataset.productTab || '').trim();
        if (label) tabLabels.add(label);
      });
      if (tabLabels.size === 0) return;

      /* Build "All" button */
      const allBtn = document.createElement('button');
      allBtn.className = 'kik-tab-filter kik-tab-filter--active';
      allBtn.dataset.filterType = 'all';
      allBtn.textContent = 'All Mattress Type';
      allBtn.addEventListener('click', () => this.filterByType('all', allBtn));
      this.tabFiltersContainer.appendChild(allBtn);

      /* Build tab buttons from product metafield (custom.tab_label) */
      tabLabels.forEach((label) => {
        const btn = document.createElement('button');
        btn.className = 'kik-tab-filter';
        btn.dataset.filterType = label;
        btn.textContent = label;
        btn.addEventListener('click', () => this.filterByType(label, btn));
        this.tabFiltersContainer.appendChild(btn);
      });
    }

    /* Move each checkbox into the product card image area and wire up click handling */
    repositionCheckboxes() {
      document.querySelectorAll('[data-compare-checkbox]').forEach((checkboxWrap) => {
        const gridItem = checkboxWrap.closest('[data-compare-product]');
        if (!gridItem) return;

        /* Find the product card image container */
        const imageWrapper = gridItem.querySelector('.product-card__inner')
          || gridItem.querySelector('.product-card__images-wrapper');
        if (!imageWrapper) return;

        /* Ensure the image wrapper is a positioning context */
        imageWrapper.style.position = 'relative';

        /* Move the checkbox element into the image wrapper */
        imageWrapper.appendChild(checkboxWrap);

        /* The product card has an <a> overlay that intercepts clicks.
           We must handle clicks on the checkbox wrapper ourselves. */
        const input = checkboxWrap.querySelector('[data-compare-input]');
        const box = checkboxWrap.querySelector('.kik-compare-check__box');
        if (!input || !box) return;

        checkboxWrap.addEventListener('click', (e) => {
          e.stopPropagation();
          e.preventDefault();
          /* Toggle the hidden input */
          input.checked = !input.checked;
          /* Fire the change event so the main handler picks it up */
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      });
    }

    filterByType(type, activeBtn) {
      /* Update active state */
      this.tabFiltersContainer.querySelectorAll('.kik-tab-filter').forEach((btn) => {
        btn.classList.remove('kik-tab-filter--active');
      });
      activeBtn.classList.add('kik-tab-filter--active');

      /* Show/hide product cards based on tab_label metafield */
      const gridItems = document.querySelectorAll('[data-compare-product]');
      gridItems.forEach((item) => {
        if (type === 'all') {
          item.style.display = '';
        } else {
          const tabLabel = (item.dataset.productTab || '').trim();
          item.style.display = tabLabel === type ? '' : 'none';
        }
      });
    }

    /* ---------- Mobile Bar Expand/Collapse ---------- */
    toggleBarExpand() {
      this.isBarExpanded = !this.isBarExpanded;
      this.bar.classList.toggle('is-expanded', this.isBarExpanded);
      if (this.chevronBtn) {
        this.chevronBtn.classList.toggle('is-expanded', this.isBarExpanded);
      }
    }

    /* ---------- Compare Mode ---------- */
    toggleCompareMode() {
      this.isCompareMode = this.toggle.checked;
      const checkboxes = document.querySelectorAll('[data-compare-checkbox]');
      checkboxes.forEach((cb) => {
        cb.style.display = this.isCompareMode ? 'block' : 'none';
      });

      if (this.isCompareMode) {
        this.bar.style.display = '';
        this.updateBar();
      } else {
        this.bar.style.display = 'none';
      }
    }

    /* ---------- Checkbox Handling ---------- */
    handleCheckboxChange(checkbox) {
      const productId = checkbox.dataset.productId;
      const card = checkbox.closest('[data-compare-product]');
      if (!card) return;

      if (checkbox.checked) {
        if (this.compareProducts.length >= this.maxProducts) {
          checkbox.checked = false;
          return;
        }
        this.addProduct(card);
      } else {
        this.removeProduct(productId);
      }

      this.updateBar();
      this.saveState();
    }

    addProduct(card) {
      const data = {
        id: card.dataset.productId,
        handle: card.dataset.productHandle,
        title: card.dataset.productTitle,
        url: card.dataset.productUrl,
        image: card.dataset.productImage,
        price: card.dataset.productPrice,
        comparePrice: card.dataset.productComparePrice,
        description: card.dataset.productDescription,
        type: card.dataset.productType,
        sizes: card.dataset.productSizes,
        thicknesses: card.dataset.productThicknesses,
        material: card.dataset.productMaterial,
      };

      /* Avoid duplicates */
      if (this.compareProducts.find((p) => p.id === data.id)) return;
      this.compareProducts.push(data);
    }

    removeProduct(productId) {
      this.compareProducts = this.compareProducts.filter((p) => p.id !== productId);
      /* Uncheck the corresponding checkbox */
      const checkbox = document.querySelector(`[data-compare-input][data-product-id="${productId}"]`);
      if (checkbox) checkbox.checked = false;
    }

    clearAll() {
      this.compareProducts = [];
      document.querySelectorAll('[data-compare-input]').forEach((cb) => { cb.checked = false; });
      this.updateBar();
      this.saveState();
    }

    /* ---------- Sticky Bar ---------- */
    updateBar() {
      const count = this.compareProducts.length;
      if (this.countEl) {
        this.countEl.textContent = `Compare (${count}/${this.maxProducts})`;
      }

      /* Show/hide bar — keep visible while in compare mode */
      if (this.isCompareMode) {
        this.bar.style.display = '';
      } else {
        this.bar.style.display = 'none';
      }

      /* Render thumbnails */
      if (this.thumbnailsEl) {
        this.thumbnailsEl.innerHTML = '';
        this.compareProducts.forEach((product) => {
          const thumb = document.createElement('div');
          thumb.className = 'kik-compare-thumb';
          /* Truncate title for display */
          const shortTitle = product.title.length > 12 ? product.title.slice(0, 12) + '...' : product.title;
          thumb.innerHTML = `
          <div class="kik-compare-thumb__inner">
            <div style="position: relative;">
              <button class="kik-compare-thumb__remove" data-remove-product="${product.id}" aria-label="Remove ${product.title}">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
              <img src="${product.image}" alt="${product.title}" class="kik-compare-thumb__img" loading="lazy">
            </div>
            <div class="kik-compare-thumb__name">${shortTitle}</div>
          </div>
          `;
          thumb.querySelector('[data-remove-product]').addEventListener('click', () => {
            this.removeProduct(product.id);
            this.updateBar();
            this.saveState();
          });
          this.thumbnailsEl.appendChild(thumb);
        });

        /* Add empty slots */
        for (let i = count; i < this.maxProducts; i++) {
          const empty = document.createElement('div');
          empty.className = 'kik-compare-thumb kik-compare-thumb--empty';
          this.thumbnailsEl.appendChild(empty);
        }
      }

      /* Render mobile thumbnails */
      if (this.thumbnailsMobileEl) {
        this.thumbnailsMobileEl.innerHTML = '';
        this.compareProducts.forEach((product) => {
          const thumb = document.createElement('div');
          thumb.className = 'kik-compare-thumb';
          thumb.innerHTML = `
          <div class="kik-compare-thumb__inner">
            <div style="position: relative;">
              <button class="kik-compare-thumb__remove" data-remove-product="${product.id}" aria-label="Remove ${product.title}">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
              <img src="${product.image}" alt="${product.title}" class="kik-compare-thumb__img" loading="lazy">
            </div>
          </div>
          `;
          thumb.querySelector('[data-remove-product]').addEventListener('click', () => {
            this.removeProduct(product.id);
            this.updateBar();
            this.saveState();
          });
          this.thumbnailsMobileEl.appendChild(thumb);
        });

        for (let i = count; i < this.maxProducts; i++) {
          const empty = document.createElement('div');
          empty.className = 'kik-compare-thumb kik-compare-thumb--empty';
          this.thumbnailsMobileEl.appendChild(empty);
        }
      }

      /* Update progress bar */
      if (this.progressEl) {
        this.progressEl.style.width = `${(count / this.maxProducts) * 100}%`;
      }
    }

    /* ---------- Comparison Modal ---------- */
    openModal() {
      if (this.compareProducts.length === 0) return;
      this.buildModalContent();
      this.modal.style.display = '';
      document.body.style.overflow = 'hidden';
    }

    closeModal() {
      this.modal.style.display = 'none';
      document.body.style.overflow = '';
    }

    buildModalContent() {
      if (!this.modalBody) return;
      const products = this.compareProducts;

      let html = '<div class="kik-compare-table">';

      /* Product header row: images + names + view details */
      html += '<div class="kik-compare-table__row kik-compare-table__row--header">';
      html += '<div class="kik-compare-table__label"></div>';
      products.forEach((p) => {
        html += `
          <div class="kik-compare-table__cell kik-compare-table__cell--product">
            <div class="kik-compare-table__product-img-wrap">
              <button class="kik-compare-table__remove" data-modal-remove="${p.id}" aria-label="Remove ${p.title}">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
              <img src="${p.image}" alt="${p.title}" class="kik-compare-table__product-img" loading="lazy">
            </div>
            <h3 class="kik-compare-table__product-name">${p.title}</h3>
            <a href="${p.url}" class="kik-compare-table__view-btn">View Details</a>
          </div>
        `;
      });
      html += '</div>';

      /* Data rows */
      const rows = [
        { label: 'Description:', key: 'description' },
        { label: 'Price', key: 'price', isPrice: true },
        { label: 'Features', key: 'description', isFeatures: true },
        { label: 'Thickness', key: 'thicknesses', isList: true },
        { label: 'Sizes', key: 'sizes', isList: true },
        { label: 'Material', key: 'material' },
      ];

      rows.forEach((row) => {
        html += '<div class="kik-compare-table__row">';
        html += `<div class="kik-compare-table__label"><strong>${row.label}</strong></div>`;
        products.forEach((p) => {
          let value = p[row.key] || '\u2014';
          if (row.isPrice) {
            const comparePrice = p.comparePrice && p.comparePrice !== '' ? `<span class="kik-compare-table__old-price">${p.comparePrice}</span>` : '';
            value = `<strong>${p.price}</strong> ${comparePrice}`;
          } else if (row.isList && value !== '\u2014') {
            const items = value.split(',').map((v) => v.trim()).filter((v) => v);
            if (items.length > 0) {
              value = '<ul>' + items.map((item) => `<li>${item}</li>`).join('') + '</ul>';
            }
          } else if (row.isFeatures) {
            /* Show description as feature bullet points */
            if (value && value !== '\u2014') {
              const sentences = value.split('.').map((s) => s.trim()).filter((s) => s);
              if (sentences.length > 1) {
                value = '<ul>' + sentences.slice(0, 3).map((s) => `<li>${s}</li>`).join('') + '</ul>';
              }
            }
          }
          html += `<div class="kik-compare-table__cell">${value}</div>`;
        });
        html += '</div>';
      });

      html += '</div>';
      this.modalBody.innerHTML = html;

      /* Attach remove button handlers in modal */
      this.modalBody.querySelectorAll('[data-modal-remove]').forEach((btn) => {
        btn.addEventListener('click', () => {
          const productId = btn.dataset.modalRemove;
          this.removeProduct(productId);
          this.updateBar();
          this.saveState();
          if (this.compareProducts.length === 0) {
            this.closeModal();
          } else {
            this.buildModalContent();
          }
        });
      });
    }

    /* ---------- Persistence ---------- */
    saveState() {
      try {
        sessionStorage.setItem('kik_compare_products', JSON.stringify(this.compareProducts));
      } catch (e) { /* ignore */ }
    }

    restoreState() {
      try {
        const stored = sessionStorage.getItem('kik_compare_products');
        if (stored) {
          this.compareProducts = JSON.parse(stored);
          /* Re-check the corresponding checkboxes */
          this.compareProducts.forEach((p) => {
            const cb = document.querySelector(`[data-compare-input][data-product-id="${p.id}"]`);
            if (cb) cb.checked = true;
          });
        }
      } catch (e) { /* ignore */ }
    }
  }

  /* Initialize compare feature when DOM is ready */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => new KikProductCompare());
  } else {
    new KikProductCompare();
  }

  // Kik: Disable deep zoom inside product modal when setting is off -------
  customElements.whenDefined('product-modal-image-wrap').then(() => {
    const Cls = customElements.get('product-modal-image-wrap');
    if (!Cls) return;
    const origMount = Cls.prototype.mountComponent;
    Cls.prototype.mountComponent = function () {
      origMount.call(this);
      const modal = this.modal;
      if (modal && modal.hasAttribute('data-kik-disable-deep-zoom')) {
        this.touchZoom = () => {};
        this.pinch = () => {};
        this.detectDoubleTap = () => {};
        if (this.image) this.image.removeAttribute('data-zoom-cursor-target');
      }
    };
  });
})();
